import * as Yup from "yup";

// Allowed data types based on backend schema
const allowedDataTypes = [
  "string", "integer", "float", "boolean", "date", "datetime",
  "email", "phone", "url", "select", "multi_select", "text"
];

// Column ID pattern - alphanumeric with underscores and hyphens
const columnIdRegExp = /^[a-zA-Z0-9_-]+$/;

// Table ID pattern - alphanumeric with underscores and hyphens
const tableIdRegExp = /^[a-zA-Z0-9_-]+$/;

// Column validation schema
const columnSchema = Yup.object().shape({
  column_id: Yup.string()
    .matches(columnIdRegExp, "Column ID can only contain letters, numbers, underscores and hyphens")
    .min(1, "Column ID is required")
    .max(50, "Column ID must be less than 50 characters")
    .required("Column ID is required"),
  
  name: Yup.string()
    .min(1, "Column name is required")
    .max(100, "Column name must be less than 100 characters")
    .required("Column name is required"),
  
  data_type: Yup.string()
    .oneOf(allowedDataTypes, "Please select a valid data type")
    .required("Data type is required"),
  
  required: Yup.boolean().default(false),
  
  unique: Yup.boolean().default(false),
  
  default_value: Yup.mixed().nullable(),
  
  max_length: Yup.number()
    .min(1, "Max length must be at least 1")
    .max(10000, "Max length cannot exceed 10000")
    .nullable()
    .when("data_type", {
      is: (dataType) => dataType === "string" || dataType === "text" || dataType === "email",
      then: () => Yup.number().min(1, "Max length is required for string fields"),
      otherwise: () => Yup.number().nullable()
    }),
  
  min_length: Yup.number()
    .min(0, "Min length cannot be negative")
    .max(10000, "Min length cannot exceed 10000")
    .nullable()
    .when("max_length", {
      is: (maxLength) => maxLength && maxLength > 0,
      then: () => Yup.number().max(Yup.ref("max_length"), "Min length cannot be greater than max length"),
      otherwise: () => Yup.number().nullable()
    }),
  
  min_value: Yup.number()
    .nullable()
    .when("data_type", {
      is: (dataType) => dataType === "integer" || dataType === "float",
      then: () => Yup.number(),
      otherwise: () => Yup.mixed().nullable()
    }),
  
  max_value: Yup.number()
    .nullable()
    .when(["data_type", "min_value"], {
      is: (dataType, minValue) => (dataType === "integer" || dataType === "float") && minValue != null,
      then: () => Yup.number().min(Yup.ref("min_value"), "Max value cannot be less than min value"),
      otherwise: () => Yup.number().nullable()
    }),
  
  options: Yup.array()
    .of(Yup.string().min(1, "Option cannot be empty"))
    .nullable()
    .when("data_type", {
      is: (dataType) => dataType === "select" || dataType === "multi_select",
      then: () => Yup.array().min(1, "At least one option is required for select fields").required("Options are required for select fields"),
      otherwise: () => Yup.array().nullable()
    }),
  
  description: Yup.string()
    .max(500, "Description must be less than 500 characters")
    .nullable(),
  
  order: Yup.number()
    .min(0, "Order cannot be negative")
    .default(0)
});

// Master table validation schema for editing (aligned with backend MasterDataTableUpdate)
export const editMasterTableSchema = Yup.object().shape({
  table_id: Yup.string()
    .matches(tableIdRegExp, "Table ID can only contain letters, numbers, underscores and hyphens")
    .min(1, "Table ID is required")
    .max(50, "Table ID must be less than 50 characters"),
  
  name: Yup.string()
    .min(1, "Table name is required")
    .max(200, "Table name must be less than 200 characters")
    .required("Table name is required"),
  
  description: Yup.string()
    .max(1000, "Description must be less than 1000 characters")
    .nullable(),
  
  category: Yup.string()
    .max(100, "Category must be less than 100 characters")
    .nullable(),
  
  access_level: Yup.string()
    .oneOf(["public", "private", "restricted"], "Please select a valid access level")
    .required("Access level is required"),
  
  allowed_roles: Yup.array()
    .of(Yup.string().min(1, "Role cannot be empty"))
    .default([]),
  
  // Make columns optional for updates (backend allows table metadata updates without column changes)
  columns: Yup.array()
    .of(columnSchema)
    .nullable()
    .test("unique-column-ids", "Column IDs must be unique", function(columns) {
      if (!columns || columns.length === 0) return true;
      
      const columnIds = columns.map(col => col.column_id).filter(Boolean);
      const uniqueIds = [...new Set(columnIds)];
      
      return columnIds.length === uniqueIds.length;
    })
    .test("unique-column-names", "Column names must be unique", function(columns) {
      if (!columns || columns.length === 0) return true;
      
      const columnNames = columns.map(col => col.name?.toLowerCase().trim()).filter(Boolean);
      const uniqueNames = [...new Set(columnNames)];
      
      return columnNames.length === uniqueNames.length;
    })
    .test("at-least-one-column-if-provided", "At least one column is required if columns are provided", function(columns) {
      // If columns array is provided, it should have at least one item
      if (Array.isArray(columns) && columns.length === 0) {
        return this.createError({ message: "If providing columns, at least one column is required" });
      }
      return true;
    })
});

// Separate schema for validating just table metadata (for metadata-only updates)
export const editMasterTableMetadataSchema = Yup.object().shape({
  name: Yup.string()
    .min(1, "Table name is required")
    .max(200, "Table name must be less than 200 characters")
    .required("Table name is required"),
  
  description: Yup.string()
    .max(1000, "Description must be less than 1000 characters")
    .nullable(),
  
  category: Yup.string()
    .max(100, "Category must be less than 100 characters")
    .nullable(),
  
  access_level: Yup.string()
    .oneOf(["public", "private", "restricted"], "Please select a valid access level")
    .required("Access level is required"),
  
  allowed_roles: Yup.array()
    .of(Yup.string().min(1, "Role cannot be empty"))
    .default([])
});

// Validation schema for individual column validation during editing
export const columnValidationSchema = columnSchema;

// Helper function to validate a single field
export const validateField = async (fieldName, value, schema = editMasterTableSchema) => {
  try {
    await schema.validateAt(fieldName, { [fieldName]: value });
    return { isValid: true, error: null };
  } catch (error) {
    return { isValid: false, error: error.message };
  }
};

// Helper function to validate the entire form
export const validateForm = async (formData) => {
  try {
    await editMasterTableSchema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    const errors = {};
    error.inner.forEach(err => {
      errors[err.path] = err.message;
    });
    return { isValid: false, errors };
  }
};

// Helper function to validate just table metadata (for updates without column changes)
export const validateTableMetadata = async (formData) => {
  try {
    await editMasterTableMetadataSchema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error) {
    const errors = {};
    error.inner.forEach(err => {
      errors[err.path] = err.message;
    });
    return { isValid: false, errors };
  }
};

// Helper function to check if form can be saved (minimum requirements)
export const canSaveForm = (formData, editFieldList = [], newFieldList = []) => {
  // Minimum requirement: table name should be provided
  const hasTableName = formData?.name?.trim()?.length > 0;
  const hasAccessLevel = formData?.access_level?.length > 0;
  
  return hasTableName && hasAccessLevel;
};

// Helper function to transform comma-separated roles to array
export const transformRolesForValidation = (formData) => {
  return {
    ...formData,
    allowed_roles: typeof formData.allowed_roles === 'string' 
      ? formData.allowed_roles.split(',').map(role => role.trim()).filter(Boolean)
      : formData.allowed_roles || []
  };
};

// Helper function to transform options string to array for columns
export const transformColumnsForValidation = (columns) => {
  return columns.map(column => ({
    ...column,
    options: typeof column.options === 'string' 
      ? column.options.split(',').map(opt => opt.trim()).filter(Boolean)
      : column.options || []
  }));
};